import React, { useEffect } from "react";
import { Icon } from "semantic-ui-react";
import { formatClock, playbackUrl } from "./catalog";
import { usePodcast } from "./PlayerContext";
import "./podcast.css";

function Art({ src, className = "" }) {
  if (src) return <img className={className} src={src} alt="" />;
  return (
    <span className={`is-empty ${className}`.trim()}>
      <Icon name="microphone" />
    </span>
  );
}

export default function HomePlayer({ episodesHref = "#pod-episodes", compact = false }) {
  const podcast = usePodcast();
  const playable = podcast.tracks.filter((track) => playbackUrl(track));
  const spotlight = podcast.current
    || playable.find((track) => track.isFeatured)
    || playable[0]
    || null;
  const album = podcast.albums[0];
  const cover = podcast.current?.posterUrl
    || podcast.current?.albumCoverUrl
    || spotlight?.posterUrl
    || spotlight?.albumCoverUrl
    || album?.coverUrl
    || "";
  const shown = podcast.current || spotlight;
  const title = shown?.title || "Stocks, markets and investing";
  const detail = shown
    ? [shown.albumTitle, shown.mood].filter(Boolean).join(" · ")
    : album
      ? `${album.title} · ${podcast.tracks.length} episode${podcast.tracks.length === 1 ? "" : "s"}`
      : "No episodes yet";
  const current = podcast.current;
  const videoMode = podcast.videoMode;
  const videoRef = podcast.videoRef;
  const nextDisabled = !podcast.current
    || podcast.queue.length <= 1
    || (!podcast.shuffle && podcast.queueIndex >= podcast.queue.length - 1);

  useEffect(() => {
    const video = videoRef.current;
    const stage = document.getElementById("pod-home-stage");
    if (!videoMode || !current || !video || !stage) return undefined;
    const place = () => {
      const rect = stage.getBoundingClientRect();
      video.style.position = "fixed";
      video.style.top = `${rect.top}px`;
      video.style.left = `${rect.left}px`;
      video.style.width = `${rect.width}px`;
      video.style.height = `${rect.height}px`;
      video.style.opacity = "1";
      video.style.zIndex = "30";
      video.style.objectFit = "cover";
      video.style.borderRadius = "12px";
      video.style.pointerEvents = "auto";
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      video.style.cssText = "";
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [current, videoMode, videoRef]);

  const start = async (track) => {
    if (!track) return;
    if (!playbackUrl(track)) {
      if (track.youtubeUrl) window.open(track.youtubeUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const queue = playable.length ? playable : [track];
    const index = Math.max(0, queue.findIndex((item) => item._id === track._id));
    try {
      await podcast.playQueue(queue, index);
    } catch (playError) {
      window.alert(playError.message || "This episode could not be started.");
    }
  };

  const onPlay = () => {
    if (podcast.current) {
      podcast.toggle().catch(() => {});
      return;
    }
    start(spotlight);
  };

  return (
    <section className="pod-feature" aria-label="Podcast player">
      <div className="pod-feature__art" id="pod-home-stage">
        {videoMode && current ? null : <Art src={cover} />}
      </div>
      <div className="pod-feature__copy">
        <div className="pod-feature__top">
          {(!compact || podcast.current) && <p className="pod-kicker">{podcast.current ? "Now playing" : "Podcast"}</p>}
        </div>
        <h2>{podcast.loading && !album && !shown ? "Loading episodes…" : title}</h2>
        <p className="pod-feature__meta">{podcast.error || detail}</p>
        {!!podcast.current && podcast.duration > 0 && (
          <div className="pod-feature__seek">
            <span>{formatClock(podcast.position)}</span>
            <input
              type="range"
              min="0"
              max={podcast.duration}
              step="1"
              value={Math.min(podcast.position, podcast.duration)}
              aria-label="Seek"
              onChange={(event) => podcast.seek(Number(event.target.value))}
            />
            <span>{formatClock(podcast.duration)}</span>
          </div>
        )}
        <div className="pod-feature__transport">
          {spotlight || podcast.current ? (
            <>
              <button type="button" onClick={() => podcast.playPrevious()} disabled={!podcast.current} aria-label="Previous">
                <Icon name="step backward" />
              </button>
              <button
                type="button"
                className="is-play"
                onClick={onPlay}
                aria-label={podcast.playing ? "Pause" : "Play"}
              >
                <Icon name={podcast.playing ? "pause" : "play"} />
              </button>
              <button type="button" onClick={() => podcast.playNext()} disabled={nextDisabled} aria-label="Next">
                <Icon name="step forward" />
              </button>
            </>
          ) : (
            !podcast.loading && <a className="pod-feature__open" href={episodesHref}>Browse episodes</a>
          )}
          {podcast.error && (
            <button type="button" className="pod-feature__open" onClick={podcast.refresh}>Try again</button>
          )}
        </div>
      </div>
    </section>
  );
}
